import { useEffect } from "react";

import { useRouter } from "next/router";

import { AssignmentsProvider } from "@/assignments/useAssignmentsGet";
import NewselaGlobalLayout from "@/base";
import RootProviders from "@/base/RootProviders";
import NotAllowedError from "@/errors/NotAllowedError";
import { SearchPageProvider as LangstonSearchPageProvider } from "@/search/Langston/SearchPageProvider";
import { SearchPage as LangstonSearchPage } from "@/search/Langston/SearchPage";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { ConceptTaxonomyProvider } from "@/search/Langston/ConceptTaxonomyProvider";

import type { NextPageContext } from "next";

const SearchPageRoute = () => {
  const { push, asPath: currentPath } = useRouter();

  const { data: user } = useUser();
  const {
    canViewSearch,
    isStudentInDistrictWithAllAdvisoryContent,
    isAuthenticatedUser,
  } = getUserProperties(user);

  useEffect(() => {
    if (!isAuthenticatedUser) {
      const signInUrl = "/signin";
      const encodedRedirectUrl = encodeURIComponent(currentPath);
      const signInAndRedirectUrl = `${signInUrl}?next=${encodedRedirectUrl}`;
      push(signInAndRedirectUrl);
    }
  }, [isAuthenticatedUser]);

  // If a user is a student in a Content Settings group that blocks search,
  // or is in an MCAT district, no searching allowed :(
  // only applies to logged in users
  if (
    isAuthenticatedUser &&
    (!canViewSearch || isStudentInDistrictWithAllAdvisoryContent)
  ) {
    return <NewselaGlobalLayout mainContents={<NotAllowedError />} />;
  }

  return (
    <NewselaGlobalLayout
      mainContents={
        <LangstonSearchPageProvider>
          <AssignmentsProvider>
            <ConceptTaxonomyProvider>
              <LangstonSearchPage />
            </ConceptTaxonomyProvider>
          </AssignmentsProvider>
        </LangstonSearchPageProvider>
      }
    />
  );
};

export default SearchPageRoute;

export async function getServerSideProps(ctx: NextPageContext) {
  const RootProvidersInitialProps = await RootProviders.__getInitialProps({
    ctx,
  });

  return {
    props: {
      [RootProviders.InitialPropsName]: RootProvidersInitialProps,
    },
  };
}
